/** THE BROWSER MEDIA ENGINE — stage 3a, a LIFT and not a rewrite.
 *
 * Every method below came out of `voiceDaemon.ts` with its logic, its ordering
 * and its comments intact. That is deliberate and it is the acceptance
 * condition Ghost signed 3a under: DESKTOP BYTE-IDENTICAL. The desktop path is
 * proven and is not being redesigned on the way past — the only change is which
 * file the statements live in and that the two facts which used to leave here
 * as socket emits now leave as callbacks, because the engine must not know
 * there is a wire (Q2).
 *
 * The session's rules are handed in (`assertLive`, `isClosing`) rather than
 * re-derived here, so the cancellation stages and the `closing` guards are the
 * same ones, in the same places, gating the same events.
 *
 * When the native engine lands at 3b it satisfies `MediaEngine` with libwebrtc
 * underneath and this file stays exactly as it is, serving desktop and any
 * non-native shell.
 */
import {
  probeBegin, probeMark, probeAttach, probeEnd, countCandidates,
} from './mediaProbe';
import { MediaEngine, MediaEngineOpts } from './mediaEngine';

// CONTROL ONLY. Media rides RTP audio tracks (Sherman F1, owner-ruled 2026-07-29):
// a data channel has no sequence numbers, timestamps, packet-loss concealment or
// adaptive bitrate — none of the properties the cellular ruling was made for.
const CONTROL_CHANNEL = 'voice-b-control';
const ICE_GATHER_TIMEOUT_MS = 5000;
const MEDIA_OPEN_TIMEOUT_MS = 8000;

function waitIceGatheringComplete(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    let timer: number;
    const done = () => {
      window.clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', onState);
      resolve();
    };
    const onState = () => {
      if (pc.iceGatheringState === 'complete') done();
    };
    pc.addEventListener('icegatheringstatechange', onState);
    timer = window.setTimeout(done, ICE_GATHER_TIMEOUT_MS);
  });
}

function waitDataChannelOpen(channel: RTCDataChannel): Promise<void> {
  if (channel.readyState === 'open') return Promise.resolve();
  return new Promise((resolve, reject) => {
    let timer: number;
    const cleanup = () => {
      window.clearTimeout(timer);
      channel.removeEventListener('open', onOpen);
      channel.removeEventListener('close', onClose);
      channel.removeEventListener('error', onError);
    };
    const onOpen = () => {
      cleanup();
      resolve();
    };
    const onClose = () => {
      cleanup();
      reject(new Error('webrtc_media_closed'));
    };
    const onError = () => {
      cleanup();
      reject(new Error('webrtc_media_error'));
    };
    channel.addEventListener('open', onOpen);
    channel.addEventListener('close', onClose);
    channel.addEventListener('error', onError);
    timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('webrtc_media_timeout'));
    }, MEDIA_OPEN_TIMEOUT_MS);
  });
}

export class BrowserMediaEngine implements MediaEngine {
  private opts: MediaEngineOpts;
  private pc: RTCPeerConnection | null = null;
  private controlChannel: RTCDataChannel | null = null;
  private stream: MediaStream | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private playbackCtx: AudioContext | null = null;
  private playbackSource: MediaStreamAudioSourceNode | null = null;
  private playbackGain: GainNode | null = null;
  private playbackBlocked = false;
  // Item 2: every peer gets a generation. An assertion made by an OLD peer's pending
  // promise must never land on a NEW track — a stale "ok" would flip a fresh
  // not-yet-asserted track into acking audio the owner cannot hear.
  private peerGeneration = 0;

  constructor(opts: MediaEngineOpts) {
    this.opts = opts;
  }

  async open(): Promise<{ type: string; sdp: string }> {
    await this.close('renegotiate');
    this.opts.assertLive('renegotiate');
    // t0 FOR THE DEVICE HALF, AND IT MUST BE BELOW the teardown. The box times
    // from the answer it sent; this times from the negotiation that asked for
    // it, so a stalled call has a number at every step instead of one gap
    // between ontrack and silence. Opened above the teardown it would be the
    // teardown's probeEnd() that closed it — the new reading marked
    // given-up before the microphone was even asked for.
    // ITS OWN NAME, NOT THE SHELL'S. This used to be
    // `probeBegin(isNativeShell() ? 'native' : 'browser')`, which labelled a
    // BROWSER-engine reading `native` whenever it ran inside the phone shell —
    // so `MEDIA·ios` was emitted by both engines and the line could not say
    // which one produced it. The device fact is still reported; it is supplied
    // by the selection site as `shell`, beside this one, instead of replacing
    // it. Dropping the branch also removes this file's last import from
    // ./nativeCall — the desktop engine now reaches nothing native at all.
    probeBegin('browser');
    // THE OWNER'S MIC AS AN RTP TRACK. Browser AEC stays on (Stage 1 parity).
    // A denied or missing microphone is a typed, visible failure — never silent.
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e) {
      this.opts.onMicUnavailable(String((e as Error)?.message ?? e));
      throw e;
    }
    // OWNED IN THE SAME TICK IT OPENED. Nothing may await between the device being
    // granted and this engine owning it.
    this.stream = stream;
    // THE DEVICE SAID YES. Everything after this is transport; everything
    // before it is permission and hardware. The track's own state travels with
    // the mark, because a track that arrives already muted is its own answer.
    probeMark('micMs', {
      trackState: stream.getAudioTracks()[0]
        ? `${stream.getAudioTracks()[0].readyState}${stream.getAudioTracks()[0].muted ? '/muted' : ''}`
        : 'no-track',
    });
    if (this.opts.isClosing()) {
      this.stopMic();
      throw new Error('voice_start_cancelled:mic');
    }
    const pc = new RTCPeerConnection({ iceServers: [] });
    stream.getAudioTracks().forEach((track) => pc.addTrack(track, stream));
    // Control only — media never rides this.
    const channel = pc.createDataChannel(CONTROL_CHANNEL);
    this.pc = pc;
    this.controlChannel = channel;
    this.wirePeer(pc, channel);
    // Sampling starts at peer creation, not at the answer: if the microphone is
    // producing nothing, that is true before the box has said a word, and the
    // reading should show it rather than start counting once media is expected.
    probeAttach(pc);
    const offer = await pc.createOffer();
    this.opts.assertLive('create_offer');
    await pc.setLocalDescription(offer);
    this.opts.assertLive('local_description');
    await waitIceGatheringComplete(pc);
    this.opts.assertLive('ice_gathering');
    probeMark('offerMs');
    if (!pc.localDescription) throw new Error('webrtc_offer_missing');
    return { type: pc.localDescription.type, sdp: pc.localDescription.sdp };
  }

  async accept(answerSdp: string): Promise<void> {
    const pc = this.pc;
    const channel = this.controlChannel;
    if (!pc || !channel) throw new Error('webrtc_no_peer_for_answer');
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    this.opts.assertLive('remote_description');
    // THE CANDIDATE COUNT, MEASURED FROM THE PHONE. The box advertises every
    // address it holds, and that set has been growing by one per day of uptime
    // (IPv6 privacy extensions, seven-day valid lifetime). Counted here, the
    // candidate filter in voice_media_edge/media.py is provable on the device
    // by a number that moves — not inferred from a call that felt faster.
    const cands = countCandidates(answerSdp);
    probeMark('answerMs', {
      remoteCandidates: cands?.unique,
      remoteCandidateLines: cands?.lines,
    });
    await waitDataChannelOpen(channel);
    this.opts.assertLive('control_channel');
    probeMark('channelMs');
  }

  /**
   * THE CLIENT'S ASSERTION — the box cannot see this window's peer, so this window
   * states it. Only a genuinely CONNECTED peer counts: a peer that is still
   * connecting, or one whose connection has failed, is not something the call can be
   * adopted onto. Fail toward REPLACE — a wrong replace costs one renegotiation, a
   * wrong adopt costs silence.
   */
  isConnected(): boolean {
    return this.pc !== null && this.pc.connectionState === 'connected';
  }

  /**
   * The microphone stream ALREADY negotiated onto the peer connection. The meter reads
   * this rather than opening a second capture — one device, one AEC instance.
   */
  micStream(): MediaStream | null {
    return this.stream;
  }

  private wirePeer(pc: RTCPeerConnection, channel: RTCDataChannel): void {
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      this.opts.onTransport({ state, transport: 'webrtc' });
      if ((state === 'failed' || state === 'disconnected') && !this.opts.isClosing()) {
        this.opts.onResumeNeeded(`webrtc_${state}`);
      }
    };
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      if ((state === 'failed' || state === 'disconnected') && !this.opts.isClosing()) {
        this.opts.onTransport({ state, transport: 'webrtc_ice' });
        this.opts.onResumeNeeded(`ice_${state}`);
      }
    };
    // THE AGENT'S VOICE ARRIVES AS A MEDIA TRACK. The browser plays what arrives; it
    // does not decide. There is no per-frame accounting here any more — the box acks a
    // mark when its samples actually leave, which the browser cannot verify and must
    // therefore never assert.
    pc.ontrack = (event: RTCTrackEvent) => {
      if (event.track.kind !== 'audio') return;
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      this.attachPlayback(stream);
      this.opts.onTransport({ state: 'connected', transport: 'webrtc_media' });
    };
    channel.onopen = () => {
      this.opts.onTransport({ state: 'connected', transport: 'webrtc_control' });
    };
    channel.onclose = () => {
      if (!this.opts.isClosing()) {
        this.opts.onTransport({ state: 'disconnected', transport: 'webrtc_control' });
        this.opts.onResumeNeeded('control_closed');
      }
    };
    channel.onerror = () => {
      if (!this.opts.isClosing()) {
        this.opts.onTransport({ state: 'failed', transport: 'webrtc_control' });
        this.opts.onResumeNeeded('control_error');
      }
    };
  }

  /**
   * PLAYBACK GRAPH — a muted <audio> element PUMPS the remote stream, and Web Audio
   * OUTPUTS it through a GainNode.
   *
   * WHY BOTH (established pattern, not invented): a remote MediaStream must be attached
   * to a media element for the browser to pull and decode it — the long-standing Chrome
   * behaviour that makes a Web-Audio-only graph go silent. The element is therefore kept
   * as the pump and MUTED, so nothing is heard twice, and the audible path is the gain
   * node. That gain node is what makes barge-in real: a gain ramp is sample-accurate and
   * immediate, whereas an element cannot be seeked and its buffered audio would keep
   * playing over the owner.
   */
  private attachPlayback(stream: MediaStream): void {
    const gen = this.peerGeneration;
    const el = this.ensureAudioElement();
    if (el.srcObject !== stream) el.srcObject = stream;
    el.muted = true;                       // PUMP ONLY — the audible path is the gain node

    // ITEM 1: THE ASSERTION COMES FROM THE AUDIBLE PATH, NEVER THE PUMP.
    // The muted element exists only because Chrome will not decode a remote stream that
    // is not attached to one. Its play() resolving proves the PUMP runs; it proves
    // nothing about audibility. Asserting "ok" from it meant a failed Web Audio graph
    // could report blocked and then be overruled by the pump's success — unheard speech
    // recorded as heard. So a pump FAILURE still reports blocked (no pump, no decode, no
    // audio), but only the running audible graph may report ok.
    void el.play().catch((e) => this.reportPlaybackBlocked(e, gen));

    void this.buildAudibleGraph(stream, gen);
  }

  /**
   * The audible path: remote stream -> GainNode -> destination. "OK" is asserted only
   * once this graph exists AND its context is actually running.
   */
  private async buildAudibleGraph(stream: MediaStream, gen: number): Promise<void> {
    try {
      const Ctor: typeof AudioContext =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) throw new Error('audio_context_unavailable');
      if (!this.playbackCtx) this.playbackCtx = new Ctor();
      const ctx = this.playbackCtx;
      if (ctx.state === 'suspended') await ctx.resume();
      if (gen !== this.peerGeneration) return;          // superseded mid-build
      try { this.playbackSource?.disconnect(); } catch {}
      const source = ctx.createMediaStreamSource(stream);
      const gain = this.playbackGain ?? ctx.createGain();
      gain.gain.setValueAtTime(1, ctx.currentTime);
      source.connect(gain);
      gain.connect(ctx.destination);
      this.playbackSource = source;
      this.playbackGain = gain;
      if (ctx.state !== 'running') {
        throw new Error(`audio_context_${ctx.state}`);
      }
      this.reportPlaybackOk(gen);
    } catch (e) {
      this.reportPlaybackBlocked(e, gen);
    }
  }

  /**
   * BARGE-IN AT THE BROWSER — a DROP, never a drain.
   *
   * The box has already stopped emitting and discarded its pending marks; this closes
   * the last gap, the audio already inside browser playout. A 5 ms ramp to zero is
   * immediate to the ear but click-free. Time still advances — the residual is silenced,
   * never played later — so nothing can play over the owner.
   */
  // `turnKey` is accepted and IGNORED. Turn-scoped gain is the device path's
  // fix (Part B); the desktop keeps this behaviour byte-for-byte because its
  // far-end reference is real and its guard works.
  duck(_turnKey?: string): void {
    const ctx = this.playbackCtx;
    const gain = this.playbackGain;
    if (!ctx || !gain) return;
    const now = ctx.currentTime;
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.005);
    } catch {}
  }

  /** The next turn's audio is starting — restore the audible path. */
  resume(_turnKey?: string): void {
    const ctx = this.playbackCtx;
    const gain = this.playbackGain;
    if (!ctx || !gain) return;
    const now = ctx.currentTime;
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(1, now + 0.005);
    } catch {}
  }

  /**
   * Re-state this window's playback fact after an adopt.
   *
   * The box drops the playback assertion whenever a window detaches, because the window
   * that made it has gone. On an adopt there is no renegotiation and therefore no
   * ontrack, so this is the only place the truth gets restated. Only the audible graph
   * may report ok — a window with no graph reports blocked, which is the safe direction.
   */
  reassertPlayback(): void {
    const running = !!this.playbackGain && this.playbackCtx?.state === 'running';
    if (running && !this.playbackBlocked) this.reportPlaybackOk();
    else {
      this.opts.onPlaybackAssert({
        blocked: true,
        detail: this.playbackBlocked ? 'playback previously reported blocked'
                                     : 'no audible graph on this window yet',
      });
    }
  }

  /**
   * The browser has stated a FACT: it cannot play. That is not the unknown jitter-buffer
   * margin the transmission ruling covers — it is a known negative, so it must reach the
   * server and stop the ACKs, or a whole reply gets recorded as heard that the owner
   * never heard one syllable of.
   */
  private reportPlaybackBlocked(e: unknown, gen?: number): void {
    if (gen !== undefined && gen !== this.peerGeneration) return;   // stale peer
    const detail = String((e as Error)?.message ?? e);
    // Always emitted — never gated on a "have I reported" latch. A fresh server track
    // starts in the not-yet-asserted state and refuses to ack until told otherwise, so
    // this statement is what makes the two sides agree, on every peer.
    this.playbackBlocked = true;
    this.opts.onPlaybackAssert({ blocked: true, detail });
    // SEAM 2 — A PLAYBACK CONDITION IS NOT A SESSION ERROR (owner ruling 2026-07-30).
    //
    // This used to emit {type:'error'}, which the hook turned into state 'error'. On
    // 2026-07-30 the audible Web Audio graph reported OK at 13:32:41.073 and the muted
    // pump element's iOS AbortError reported blocked at 41.327 — and the call went on
    // to forward its first frame at 42.328. The call was working perfectly and the
    // surface said error, which invited the retry tap that started a second session on
    // top of a live one.
    //
    // It stays VISIBLE — nothing goes silent — but it is transport state, which is
    // what the server has always correctly called it. It must never invite a restart.
    this.opts.onTransport({
      state: 'playback_blocked',
      transport: 'device_audio',
      detail,
    });
  }

  /**
   * Playback is confirmed running. Reported as clearly as a failure is: without it the
   * server never leaves the not-yet-asserted state, marks are never acked, and speech
   * the owner genuinely heard is lost from the transcript.
   */
  private reportPlaybackOk(gen?: number): void {
    if (gen !== undefined && gen !== this.peerGeneration) return;   // stale peer
    const wasBlocked = this.playbackBlocked;
    this.playbackBlocked = false;
    this.opts.onPlaybackAssert({ blocked: false, detail: '' });
    if (wasBlocked) {
      // A recovery is a real state change — visible, never silent.
      this.opts.onTransport({
        state: 'playback_restored',
        transport: 'device_audio',
      });
    }
  }

  private ensureAudioElement(): HTMLAudioElement {
    if (this.audioEl) return this.audioEl;
    const el = document.createElement('audio');
    el.autoplay = true;
    el.muted = true;
    el.setAttribute('playsinline', 'true');
    el.style.display = 'none';
    document.body.appendChild(el);
    this.audioEl = el;
    return el;
  }

  /** Release the device microphone. Never hold it open across a renegotiation, a
   *  cancelled start, or a failed one. Safe to call more than once. */
  private stopMic(): void {
    const mic = this.stream;
    this.stream = null;
    if (!mic) return;
    try { mic.getTracks().forEach((t) => t.stop()); } catch {}
  }

  async close(reason: string): Promise<void> {
    const pc = this.pc;
    this.pc = null;
    this.controlChannel = null;
    // The server's next track starts NOT-YET-ASSERTED, so nothing here needs to carry a
    // stale opinion across the peer change: attachPlayback() re-states the real outcome
    // on the new peer. Clearing the graph refs makes duck/resume no-ops until it does.
    // Bumping the generation invalidates any assertion still in flight from this peer.
    this.peerGeneration += 1;
    this.playbackBlocked = false;
    try { this.playbackSource?.disconnect(); } catch {}
    this.playbackSource = null;
    // The probe never outlives its peer, AND IT RECORDS WHY IT ENDED. A window
    // superseded by another one tears this peer down mid-negotiation, and the
    // reading that produces is about the teardown — not about ICE, not about the
    // microphone. Without the reason it reads identically to a peer that simply
    // never sent anything, which is how the 2026-08-05 desktop control was
    // misread as transport failure.
    probeEnd(reason);
    this.stopMic();
    if (this.audioEl) {
      try { this.audioEl.srcObject = null; } catch {}
    }
    if (pc) {
      try { pc.onconnectionstatechange = null; } catch {}
      try { pc.oniceconnectionstatechange = null; } catch {}
      try { pc.ontrack = null; } catch {}
      try { pc.close(); } catch {}
    }
  }
}
